import { z } from "zod";
import { onboardingLiveCatalogSchema, type OnboardingLiveCatalog } from "./onboardingLiveCatalog";

import { onboardingMarketIds } from "@/features/onboarding/onboardingCatalog";
import {
  getOnboardingQuestionSequence,
  onboardingProfileIds,
  onboardingQuestionIds,
  onboardingShopStatusIds,
  type OnboardingState,
} from "@/features/onboarding/onboardingSlice";
import { baseApi } from "@/shared/api/baseApi";

export const onboardingSchemaVersion = 1;
export const onboardingFlowVersion = "merry-business-profile-v1";

const nullableEnum = <TValues extends readonly [string, ...string[]]>(
  values: TValues,
) => z.enum(values).nullable();

const onboardingAnswersSchema = z
  .object({
    a: z
      .object({
        advantage_ids: z.array(z.string()),
        other_advantage: z.string(),
      })
      .strict(),
    b: z
      .object({
        alert_timing: nullableEnum([
          "after-no-drop",
          "when-clear",
          "show-all-with-risk",
          "no-preference",
        ] as const),
        growth_window: nullableEnum([
          "thirty-days",
          "fourteen-days",
          "seven-days",
          "self-judge",
          "unsure",
        ] as const),
        start_method: nullableEnum([
          "dropshipping",
          "small-stock",
          "inventory-7-14-days",
          "overseas-warehouse",
          "no-fixed-method",
        ] as const),
      })
      .strict(),
    c: z
      .object({
        advantage_ids: z.array(z.string()),
        other_advantage: z.string(),
        price_range: nullableEnum([
          "low-volume",
          "mass-market",
          "mid-premium",
          "premium-professional",
          "undecided",
        ] as const),
        primary_goal: nullableEnum([
          "find-category-price",
          "find-product-complaints",
          "validate-demand",
          "find-selling-points",
          "differentiate-competitors",
          "undecided",
        ] as const),
        product_stage: nullableEnum([
          "exploring-direction",
          "validating-idea",
          "preparing-sample",
          "testing-market",
          "selling-branded-product",
        ] as const),
      })
      .strict(),
    category_ids: z.array(z.string()),
    completed_question_ids: z.array(z.enum(onboardingQuestionIds)),
    d: z
      .object({
        content_goal: nullableEnum([
          "recent-product-opportunities",
          "valuable-markets-categories",
          "find-suitable-product",
          "start-store-business",
          "improve-existing-store",
          "browse-first",
        ] as const),
        entry_state: nullableEnum([
          "explore-opportunities",
          "considering-store",
          "store-no-product",
          "selling-seeking-growth",
        ] as const),
      })
      .strict(),
    market_ids: z.array(z.enum(onboardingMarketIds)),
    other_category: z.string(),
    shop_status: z.enum(onboardingShopStatusIds).nullable(),
  })
  .strict();

export const onboardingProfileSchema = z
  .object({
    answers: onboardingAnswersSchema,
    completed_at: z.string().nullable(),
    created_at: z.string(),
    flow_version: z.literal(onboardingFlowVersion),
    primary_profile_id: z.enum(onboardingProfileIds).nullable(),
    resume_question_id: z.enum(onboardingQuestionIds).nullable(),
    revision: z.number().int().positive(),
    schema_version: z.literal(onboardingSchemaVersion),
    selected_profile_ids: z.array(z.enum(onboardingProfileIds)),
    status: z.enum(["draft", "completed"]),
    updated_at: z.string(),
  })
  .strict();

export type OnboardingProfile = z.infer<typeof onboardingProfileSchema>;

const onboardingProfileResponseSchema = z
  .object({ profile: onboardingProfileSchema.nullable() })
  .strict();

export interface OnboardingProfileMutation {
  answers: z.infer<typeof onboardingAnswersSchema>;
  expected_revision: number;
  flow_version: typeof onboardingFlowVersion;
  mutation_id: string;
  primary_profile_id: OnboardingState["primaryProfileId"];
  resume_question_id: (typeof onboardingQuestionIds)[number] | null;
  schema_version: typeof onboardingSchemaVersion;
  selected_profile_ids: OnboardingState["selectedProfileIds"];
}

export function onboardingProfileToState(
  profile: OnboardingProfile,
): OnboardingState {
  return {
    primaryProfileId: profile.primary_profile_id,
    questionFlow: {
      a: {
        advantageIds: profile.answers.a.advantage_ids,
        otherAdvantage: profile.answers.a.other_advantage,
      },
      b: {
        alertTiming: profile.answers.b.alert_timing,
        growthWindow: profile.answers.b.growth_window,
        startMethod: profile.answers.b.start_method,
      },
      c: {
        advantageIds: profile.answers.c.advantage_ids,
        otherAdvantage: profile.answers.c.other_advantage,
        priceRange: profile.answers.c.price_range,
        primaryGoal: profile.answers.c.primary_goal,
        productStage: profile.answers.c.product_stage,
      },
      categoryIds: profile.answers.category_ids,
      completedQuestionIds: profile.answers.completed_question_ids,
      d: {
        contentGoal: profile.answers.d.content_goal,
        entryState: profile.answers.d.entry_state,
      },
      marketIds: profile.answers.market_ids,
      otherCategory: profile.answers.other_category,
      shopStatus: profile.answers.shop_status,
    },
    selectedProfileIds: profile.selected_profile_ids,
  };
}

export function buildOnboardingProfileMutation(
  state: OnboardingState,
  expectedRevision: number,
): OnboardingProfileMutation {
  const sequence = getOnboardingQuestionSequence({
    primaryProfileId: state.primaryProfileId,
    selectedProfileIds: state.selectedProfileIds,
  });
  const completedCount = state.questionFlow.completedQuestionIds.length;

  return {
    answers: {
      a: {
        advantage_ids: state.questionFlow.a.advantageIds,
        other_advantage: state.questionFlow.a.otherAdvantage,
      },
      b: {
        alert_timing: state.questionFlow.b.alertTiming,
        growth_window: state.questionFlow.b.growthWindow,
        start_method: state.questionFlow.b.startMethod,
      },
      c: {
        advantage_ids: state.questionFlow.c.advantageIds,
        other_advantage: state.questionFlow.c.otherAdvantage,
        price_range: state.questionFlow.c.priceRange,
        primary_goal: state.questionFlow.c.primaryGoal,
        product_stage: state.questionFlow.c.productStage,
      },
      category_ids: state.questionFlow.categoryIds,
      completed_question_ids: state.questionFlow.completedQuestionIds,
      d: {
        content_goal: state.questionFlow.d.contentGoal,
        entry_state: state.questionFlow.d.entryState,
      },
      market_ids: state.questionFlow.marketIds as Array<
        (typeof onboardingMarketIds)[number]
      >,
      other_category: state.questionFlow.otherCategory,
      shop_status: state.questionFlow.shopStatus,
    },
    expected_revision: expectedRevision,
    flow_version: onboardingFlowVersion,
    mutation_id: createMutationId(),
    primary_profile_id: state.primaryProfileId,
    resume_question_id: sequence[completedCount] ?? null,
    schema_version: onboardingSchemaVersion,
    selected_profile_ids: state.selectedProfileIds,
  };
}

function createMutationId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `mutation-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export const onboardingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOnboardingCatalog: builder.query<OnboardingLiveCatalog, string>({
      query: () => "/v1/onboarding/catalog",
      transformResponse: (response) => onboardingLiveCatalogSchema.parse(response),
    }),
    completeOnboardingProfile: builder.mutation<
      { profile: OnboardingProfile },
      OnboardingProfileMutation
    >({
      query: (body) => ({
        body: { ...body, resume_question_id: null },
        method: "POST",
        url: "/v1/onboarding/profile/complete",
      }),
      transformResponse: (response) => {
        const parsed = onboardingProfileResponseSchema.parse(response);
        if (!parsed.profile) {
          throw new Error("完成画像响应缺少 profile");
        }
        return { profile: parsed.profile };
      },
    }),
    getOnboardingProfile: builder.query<
      { profile: OnboardingProfile | null },
      void
    >({
      query: () => "/v1/onboarding/profile",
      transformResponse: (response) =>
        onboardingProfileResponseSchema.parse(response),
    }),
    saveOnboardingProfile: builder.mutation<
      { profile: OnboardingProfile },
      OnboardingProfileMutation
    >({
      query: (body) => ({
        body,
        method: "PUT",
        url: "/v1/onboarding/profile",
      }),
      transformResponse: (response) => {
        const parsed = onboardingProfileResponseSchema.parse(response);
        if (!parsed.profile) {
          throw new Error("保存画像响应缺少 profile");
        }
        return { profile: parsed.profile };
      },
    }),
  }),
});
